import { useState, useContext, useEffect } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";

import BasicDetails from "./settings/BasicDetails";
import GeneralSettings from "./settings/GeneralSettings";
import ChatbotAppearance from "./settings/ChatbotAppearance";
import KnowledgeBase from "./settings/KnowledgeBase";
import PreAISettings from "./settings/PreAISettings";
import AISettings from "./settings/AISettings";

import GeneralPreview from "./preview/GeneralPreview";
import BotIconSettings from "./preview/BotIconSettings";
import FormPreview from "./preview/FormPreview";

import { withSnackbar } from "../../utils/common/SnackbarHoc";

import { Http } from "../../../src/utils";
import { UserContext } from "../../context/userContext";
import { UserLoginInfo } from "../../types/context";

import { useNavigate, useParams } from "react-router-dom";
import {
  IProductParams,
  ChatAgentSettingIF,
  ChatbotBrandingIF,
  ChatbotAISettingIF,
} from "../../model";
import APIIntegrationSettings from "./settings/APIIntegrationSettings";

import { cloneDeep } from "lodash";
import PlaygroundPreview from "./preview/PlaygroundPreview";

import { handleMandatoryValidation } from "./helpers";
import { Alert, Stack } from "@mui/material";
import { uploadFileBucketName } from "../../utils/config";
import Joyride, {
  CallBackProps,
  STATUS,
  Step,
  StoreHelpers,
  ACTIONS,
} from "react-joyride";
import images from "../../global-styles/images";
function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const accordionSettingsInitialValues = {
  basicDetails: false,
  generalSettings: true,
  chatbotAppearance: false,
  knowledgeBase: false,
  preAISettings: false,
  AISettings: false,
  apiIntegrationSettings: false,
};

const accordionResetInitialValues = {
  basicDetails: false,
  generalSettings: false,
  chatbotAppearance: false,
  knowledgeBase: false,
  preAISettings: false,
  AISettings: false,
  apiIntegrationSettings: false,
};

const chatbotAiSettingsInitialValues = {
  llmArchitecture: "gpt3",
  tone: "polite",
  useEmoji: false,
  allowGeneralQuestions: true,
  lengthOfResponse: 150,
  numberOfTriesBeforeRedirect: 0,
  customerSupportResponse: "",
  aiCreativity: 0.5,
  intelligenceLayer: [],
  kbArticles: [],
  openaiKey: "",
};

const chatbotAppearanceInitialValues = {
  "cuvo-chatbot-footer-bg-color": "#f4f7f9",
  "cuvo-chatbot-header-bg-color": "#2858D3", // primary color
  "cuvo-chatbot-header-text-color": "#FFFFFF", // black or white based on primary color contrast
  "cuvo-chatbot-header-text-font-family": "",
  "cuvo-chatbot-header-text-font-weight": "500",
  "cuvo-chatbot-header-text-font-style": "normal",
  "cuvo-chatbot-header-text-font-size": "16px",
  "cuvo-chatbot-body-bg-color": "#FFFFFF", // constant color (always white)
  "cuvo-chatbot-bot-bg-color": "#f4f7f9", // bot message box color
  "cuvo-chatbot-bot-text-color": "#000000", // black or white based on bot message box color contrast
  "cuvo-chatbot-bot-text-font-family": "",
  "cuvo-chatbot-bot-text-font-weight": "500",
  "cuvo-chatbot-bot-text-font-style": "normal",
  "cuvo-chatbot-bot-text-font-size": "12px",
  "cuvo-chatbot-user-bg-color": "#2858D3", // primary color
  "cuvo-chatbot-user-text-color": "#FFFFFF", // black or white based on primary color contrast
  "cuvo-chatbot-user-text-font-family": "",
  "cuvo-chatbot-user-text-font-weight": "500",
  "cuvo-chatbot-user-text-font-style": "normal",
  "cuvo-chatbot-user-text-font-size": "12px",
  "cuvo-chatbot-input-bg-color": "#FFFFFF",
  "cuvo-chatbot-input-text-color": "#202124cc",
  "cuvo-chatbot-input-text-font-family": "",
  "cuvo-chatbot-input-text-font-weight": "500",
  "cuvo-chatbot-input-text-font-style": "normal",
  "cuvo-chatbot-input-text-font-size": "12px",
  "cuvo-chatbot-send-icon-fg-color": "#FFFFFF",
  "cuvo-chatbot-send-icon-bg-color": "#2858D3", // action color
  "cuvo-chatbot-bot-icon-fg-color": "#FFFFFF",
  "cuvo-chatbot-bot-icon-bg-color": "#2858D3", // primary color
  "cuvo-chatbot-bot-action-bg-color": "#2858D380", // action color with transparency(To add transparency, add two additional digits between 00 and FF)
  "cuvo-chatbot-bot-action-text-color": "#2858D3", // action color
  "cuvo-chatbot-bot-action-text-font-family": "",
  "cuvo-chatbot-bot-action-text-font-weight": "500",
  "cuvo-chatbot-bot-action-text-font-style": "normal",
  "cuvo-chatbot-bot-action-text-font-size": "12px",
  "cuvo-chatbot-primary-button-bg-color": "#2858D350", // primary color with transparency(To add transparency, add two additional digits between 00 and FF)
  "cuvo-chatbot-primary-button-text-color": "#2858D3", //primary color
  "cuvo-chatbot-primary-button-text-font-family": "",
  "cuvo-chatbot-primary-button-text-font-style": "normal",
  "cuvo-chatbot-primary-button-text-font-weight": "400",
  "cuvo-chatbot-primary-button-text-font-size": "14px",
  "cuvo-chatbot-secondary-button-bg-color": "#f4f7f9", // bot message box color
  "cuvo-chatbot-secondary-button-text-color": "#2858D3", // primary color
  "cuvo-chatbot-secondary-button-text-font-family": "",
  "cuvo-chatbot-secondary-button-text-font-style": "normal",
  "cuvo-chatbot-secondary-button-text-font-weight": "400",
  "cuvo-chatbot-secondary-button-text-font-size": "14px",
};
const chatbotSettingsInitialValues: any = {
  name: "",
  chatbotIcon: "",
  chatbotIconReq: "",
  welcomeMsg: "",
  sendIcon: "",
  sendIconReq: "",
  sessionTimeout: 10,
  chatIcon: "",
  chatIconReq: "",
};

const initialKnowledgebaseValues = {
  url: "",
  file: null,
};

const AddChatbot = (props: any) => {
  const { snackbarShowMessage } = props;
  const { productId, versionId } = useParams();
  const { userInfo, orgId } = useContext(UserContext) as UserLoginInfo;

  const [chatbotSettings, setChatbotSettings] = useState<ChatAgentSettingIF>(
    chatbotSettingsInitialValues
  );

  const [chatbotAISettings, setChatbotAISettings] =
    useState<ChatbotAISettingIF>(chatbotAiSettingsInitialValues);

  const [chatbotAppearance, setChatbotAppearance] = useState<ChatbotBrandingIF>(
    chatbotAppearanceInitialValues
  );

  const [accordionSettings, setAccordiongSettings] = useState(
    accordionSettingsInitialValues
  );

  const [product, setProduct] = useState({} as any);
  const [kbArticles, setKBArticles] = useState([]);
  const [ChatSessionuuid, setChatSessionuuid] = useState("");
  const [WcMessages, setWcMessages] = useState<any>("");
  const [showAlertForPrevPlayground, setshowAlertForPrevPlayground] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // const [chatbotTheme, setChatbotTheme] = useState(chatThemeDefaultValue);

  const [tab] = useState(0);
  const navigate = useNavigate();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [run, setRun] = useState(false);

  useEffect(() => {
    setChatSessionuuid(GenerateUUID());
  }, []);

  const GenerateUUID = () => {
    return (
      "session_id_" +
      "xxxxxx_xxxxx_xxxx_yxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      })
    );
  };

  const handleFetchArticles = async () => {
    const res: any = await Http.get({
      url: `/api/v2/kbArticles/${productId}/${versionId}`,
    });

    if (res?.kbArticles) {
      const kbFolders = res.kbArticles.filter(
        (article: any) => article.type === "folder"
      );
      const kbData = kbFolders.map((e: any) => {
        return { name: e.name, id: e.id };
      });
      setKBArticles(kbData);
    }
  };

  const handleFetchProduct = async () => {
    setIsLoading(true);
    const res: any = await Http.get({
      url: `/api/v2/products/${productId}/${versionId}`,
    });

    if (res?.products?.[0]?.chatAgentSettings) {
      setshowAlertForPrevPlayground(false);
      const productRes = res.products[0];
      const { chatAgentSettings = {} } = productRes;
      const {
        name,
        chatIcon,
        chatbotIcon,
        sendIcon,
        welcomeMsg,
        aiSetting,
        branding,
      } = chatAgentSettings;

      const tempchatBotSettings = {
        name,
        chatIcon,
        chatbotIcon,
        sendIcon,
        welcomeMsg,
      };

      const tempAiSettings = {
        ...aiSetting,
        kbArticles: aiSetting.kbArticles
          ? aiSetting.kbArticles.map((article: any) => article.id)
          : [],
      };

      setChatbotAppearance(branding);
      setChatbotAISettings(tempAiSettings);
      setChatbotSettings(tempchatBotSettings);
      setProduct(productRes);
      setIsLoading(false);
    } else {
      setshowAlertForPrevPlayground(true);

      const productRes = res.products[0];
      const prodName = res?.products?.[0].name;

      const name = `${prodName} bot`;
      const welcomeMsg = `Welcome to ${prodName} bot`;
      const chatbotIcon = `https://${uploadFileBucketName}.s3.amazonaws.com/chatbotIcon.png`;
      const sendIcon = `https://${uploadFileBucketName}.s3.amazonaws.com/sendIcon.png`;
      const chatIcon = `https://${uploadFileBucketName}.s3.amazonaws.com/chatIcon.png`;
      const tempChatInitialValues = {
        ...chatbotSettingsInitialValues,
        name,
        welcomeMsg,
        chatbotIcon,
        sendIcon,
        chatIcon,
      };
      setProduct(productRes);
      setChatbotSettings(tempChatInitialValues);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    setWcMessages(chatbotSettings.welcomeMsg);
  }, [chatbotSettings.welcomeMsg]);
  useEffect(() => {
    handleFetchProduct();
    handleFetchArticles();
  }, []);

  const handleAccordionSettings = (name: string) => {
    setAccordiongSettings((prevState) => {
      return {
        ...accordionResetInitialValues,
        [name]: !prevState[name as keyof typeof accordionResetInitialValues],
      };
    });
  };

  const handleBasicSettingsValidation = () => {
    if (!chatbotSettings.name) {
      snackbarShowMessage("You must provide chatbot name", "error", 3000);
      return true;
    }

    if (!chatbotSettings.chatbotIcon) {
      snackbarShowMessage("You must upload a icon for chatbot", "error", 3000);
      return true;
    }

    return false;
  };

  const handleGeneralSettingsValidation = () => {
    if (!chatbotSettings.welcomeMsg) {
      snackbarShowMessage(
        "You must provide chatbot welcome message",
        "error",
        3000
      );
      return true;
    }

    return false;
  };

  const handleCreateChatbot = async () => {
    const tempProduct = cloneDeep(product);

    try {
      const tempChatbotAISettings = {
        ...chatbotAISettings,
        kbArticles: kbArticles.filter((article: any) =>
          chatbotAISettings.kbArticles.includes(article.id)
        ),
      };

      if (tempChatbotAISettings.numberOfTriesBeforeRedirect <= 0) {
        tempChatbotAISettings.customerSupportResponse = "";
      }
      if (tempChatbotAISettings.intelligenceLayer) {
        tempChatbotAISettings.intelligenceLayer =
          tempChatbotAISettings.intelligenceLayer.map((e) => ({
            ...e,
            question_embedding: [],
          }));
      }
      const newChatAgentSettings = {
        ...chatbotSettings,
        branding: chatbotAppearance,
        aiSetting: tempChatbotAISettings,
      };

      const newProduct = {
        ...tempProduct,
        chatAgentSettings: newChatAgentSettings,
      };

      const data = { products: [{ ...newProduct }] };

      if (
        handleMandatoryValidation(
          kbArticles,
          newChatAgentSettings,
          snackbarShowMessage
        )
      ) {
        const tempProduct = cloneDeep(product);
        const tempChatbotAISettings = cloneDeep(chatbotAISettings);
        if (tempChatbotAISettings.numberOfTriesBeforeRedirect <= 0) {
          tempChatbotAISettings.customerSupportResponse = "";
          setChatbotAISettings(tempChatbotAISettings);
        }
        if (tempChatbotAISettings.intelligenceLayer) {
          tempChatbotAISettings.intelligenceLayer =
            tempChatbotAISettings.intelligenceLayer.map((e) => ({
              ...e,
              question_embedding: [],
            }));
          setChatbotAISettings(tempChatbotAISettings);
        }
        const newChatAgentSettings = {
          ...chatbotSettings,
          branding: chatbotAppearance,
          aiSetting: tempChatbotAISettings,
        };
        setChatbotSettings(newChatAgentSettings);
        const newProduct = {
          ...tempProduct,
          chatAgentSettings: newChatAgentSettings,
        };
        setProduct(newProduct);
        Http.put({
          url: `/api/v2/products`,
          body: {
            ...data,
          },
          state: userInfo,
        })
          .then((response: any) => {
            if (response && response.message === "Product already exists") {
              snackbarShowMessage("Product already exists", "error", 3000);
            } else {
              setshowAlertForPrevPlayground(false);

              snackbarShowMessage(
                "product with chatbot settings saved successfully",
                "success",
                3000
              );
              setChatSessionuuid(GenerateUUID());
              if (product?.products?.[0] && !product?.products?.[0]?.apiKey) {
                setAccordiongSettings({
                  ...accordionResetInitialValues,
                  apiIntegrationSettings: true,
                });
              }
            }
          })
          .catch((error: any) => {
            snackbarShowMessage(error.message, "error");
          });
      }
    } catch (err) {
      snackbarShowMessage("chatbot creation error", "error", 3000);
    }
  };

  const handleCancleChatbot = () => {
    if (product) {
      // setChatbotSettings(chatbotSettingsInitialValues);
      navigate("/add-account", {
        state: {
          id: product.id,
          version: product.version,
        },
      });
    }
  };
  const handleJoyrideCallback = (data: any) => {
    const { action, index, status, type } = data;
    localStorage.setItem("setWalkthroughStepIndex", index);
    if (action == ACTIONS.PREV) {
      const element: any = document.getElementById("test-in");
      element.scrollTop = element.scrollHeight;
    }
    if (
      status == STATUS.FINISHED ||
      action == ACTIONS.CLOSE ||
      action == ACTIONS.SKIP
    ) {
      if (ACTIONS.SKIP == "skip") {
        setTooltipIsOpen(true);
        setTimeout(() => {
          setTooltipIsOpen(false);
        }, 3000);
      }
      setRun(false);
      localStorage.removeItem("setWalkthroughStepIndex");
    }
  };
  const renderGuidedSteps = () => {
    // let steps: any;
    //edit form steps
    const steps = [
      {
        disableBeacon: true,
        target: ".guideTourChatThemeBrandngSettings",
        content: (
          <div>
            <h3 className="guidesteptitle">Configure chatbot</h3>
            <h4 className="guidestepsubtitle">
              Step 1: Click on 'next' in each settings section to configure the
              behavior and look of the CuVo Chat agent which will be a part of
              your product experience. These sections come with default
              configurations that you can modify at any time. Changes don’t
              require redeployment of the CuVo Chat agent.
            </h4>
          </div>
        ),
      },
      {
        target: ".guideTourViewActualSize",
        content: (
          <div>
            <h3 className="guidesteptitle">Configure chatbot</h3>
            <h4 className="guidestepsubtitle">
              Step 2: You can preview your CuVo Chat agent configuration in this
              section. Click this link to enlarge the preview in a new window
            </h4>
          </div>
        ),
      },
      {
        target: ".guideTourCreateSave",
        content: (
          <div>
            <h3 className="guidesteptitle">Configure chatbot</h3>
            <h4 className="guidestepsubtitle">
              Step 3: Finally click the ‘save’ button to save your chat’s
              configurations. The integration ‘steps’ section below will guide
              on how to integrate your CuVo chat agent into your chat’s code.
              The steps may vary based on the technology and target device on
              your product.
            </h4>
          </div>
        ),
      },
    ];
    return steps;
  };
  const openGuidedtour = () => {
    const element: any = document.getElementById("test-in");
    element.scrollTop = element.scrollHeight;
    setRun(!run);
  };
  return (
    <Box
      className={isLoading ? "add-products-wrap loader" : "add-products-wrap"}
    >
      <Joyride
        locale={{
          open: "Click to see the steps",
        }}
        run={run}
        callback={handleJoyrideCallback}
        // disableScrolling={true}
        styles={{
          // overlayLegacyCenter: {
          //   backgroundColor: "red",
          // },
          overlay: {
            height: "100vh",
          },
          options: {
            // modal arrow and background color
            arrowColor: "#0157ff",
            backgroundColor: "#0157ff",
            // page overlay color
            overlayColor: "#4f4f4f",
            //button color
            primaryColor: "#d68520",
            //text color
            textColor: "#fff",

            //width of modal
            width: 300,
            //zindex of modal
            zIndex: 1000,
          },
        }}
        continuous
        showProgress
        showSkipButton
        steps={renderGuidedSteps()}
      />
      <TabPanel value={tab} index={0}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            lg={6}
            id="test-in"
            className="add-form-wrap show"
            // className={`add-form-wrap ${open ? "show" : "hide"}`}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={tab} aria-label="tabs">
                <Tab label={"Configure chatbot"} {...a11yProps(0)} />

                <Button
                  type="button"
                  variant="outlined"
                  sx={{ textTransform: "uppercase" }}
                  className="create-pro-btn "
                  id="pro-cancel"
                  onClick={handleCancleChatbot}
                >
                  close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: "uppercase" }}
                  className="create-pro-btn guideTourCreateSave"
                  id="pro-save"
                  onClick={handleCreateChatbot}
                >
                  save
                </Button>
              </Tabs>
            </Box>
            <Box className="accordion-wrap">
              {showAlertForPrevPlayground && (
                <Stack sx={{ width: "100%" }} pt={2}>
                  <Alert severity="info">
                    Please <b>Save</b> the Chat Settings, to see the Integration
                    steps!
                  </Alert>
                </Stack>
              )}
              <BasicDetails
                name="basicDetails"
                chatbotSettings={chatbotSettings}
                setChatbotSettings={setChatbotSettings}
                accordionSettings={accordionSettings}
                handleAccordionSettings={handleAccordionSettings}
                handleBasicSettingsValidation={handleBasicSettingsValidation}
                productParams={product}
              />

              <PreAISettings
                name="preAISettings"
                chatbotAISettings={chatbotAISettings}
                setChatbotAISettings={setChatbotAISettings}
                accordionSettings={accordionSettings}
                handleAccordionSettings={handleAccordionSettings}
              />

              <AISettings
                name="AISettings"
                chatbotAISettings={chatbotAISettings}
                setChatbotAISettings={setChatbotAISettings}
                accordionSettings={accordionSettings}
                handleAccordionSettings={handleAccordionSettings}
                kbArticles={kbArticles}
              />
              <GeneralSettings
                name="generalSettings"
                chatbotSettings={chatbotSettings}
                setChatbotSettings={setChatbotSettings}
                accordionSettings={accordionSettings}
                handleAccordionSettings={handleAccordionSettings}
                product={product}
              />
              <ChatbotAppearance
                name="chatbotAppearance"
                chatbotAppearance={chatbotAppearance}
                accordionSettings={accordionSettings}
                handleAccordionSettings={handleAccordionSettings}
                handleGeneralSettingsValidation={
                  handleGeneralSettingsValidation
                }
                setChatbotAppearance={setChatbotAppearance}
                showAlertForPrevPlayground={showAlertForPrevPlayground}
                product={product}
              />
              {/* <AISettings
                name="AISettings"
                chatbotAiSettings={chatbotSettings?.aiSetting}
                setChatbotAiSettings={setChatbotAiSettings}
                accordionSettings={accordionSettings}
                handleAccordionSettings={handleAccordionSettings}
                handleGeneralSettingsValidation={
                  handleGeneralSettingsValidation
                }
              /> */}
              {!showAlertForPrevPlayground &&
                product?.id &&
                product?.version &&
                !product.cloneNewVersion && (
                  <APIIntegrationSettings
                    name="apiIntegrationSettings"
                    productParams={product}
                    setProductParams={setProduct}
                    accordionSettings={accordionSettings}
                    handleAccordionSettings={handleAccordionSettings}
                  />
                )}
            </Box>
          </Grid>

          <Grid item xs={12} lg={6} className="faq-wrap">
            <List component="div" disablePadding sx={{ position: "unset" }}>
              <Box style={{ width: "100%" }}>
                <div className="header-right-menu">
                  <Typography className="title-sm">
                    Preview/Playground
                  </Typography>
                </div>
                <div className="content-right-menu">
                  <Typography className="info">
                    Click on Pre AI Settings or AI Settings to view the
                    Playground.
                    <br />
                    Click on Text Settings or Theme and Branding to view Preview
                  </Typography>

                  {(accordionSettings["generalSettings"] ||
                    accordionSettings["chatbotAppearance"]) && (
                    <GeneralPreview
                      chatbotSettings={chatbotSettings}
                      chatbotAppearance={chatbotAppearance}
                    />
                  )}

                  {/* {(accordionSettings["generalSettings"] ||
                    accordionSettings["chatbotAppearance"]) && (
                    <BotIconSettings
                      chatbotSettings={chatbotSettings}
                      chatbotAppearance={chatbotAppearance}
                    />
                  )} */}
                  {Object.keys(product).length > 0 && (
                    <PlaygroundPreview
                      show={accordionSettings}
                      productParams={product}
                      chatbotSettings={chatbotSettings}
                      wcmsg={WcMessages}
                      ChatSessionuuid={ChatSessionuuid}
                      chatbotAppearance={chatbotAppearance}
                      showAlertForPrevPlayground={showAlertForPrevPlayground}
                    />
                  )}
                  {(accordionSettings["generalSettings"] ||
                    accordionSettings["chatbotAppearance"]) && (
                    <FormPreview
                      chatbotSettings={chatbotSettings}
                      chatbotAppearance={chatbotAppearance}
                    />
                  )}
                </div>
              </Box>
            </List>
          </Grid>
        </Grid>
        <Button
          variant="contained"
          onClick={() => openGuidedtour()}
          className="walkthrough-btn"
        >
          <img src={images.walkthrough} /> &nbsp; Walk Through
          {/* animate-charcter */}
        </Button>
      </TabPanel>
    </Box>
  );
};

export default withSnackbar(AddChatbot);
